// @flow
import React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
// $FlowFixMe - TS file
import env from 'bench-environment';
import { graphqlClient } from 'bk-web-app';
import SidebarCompanyAvatarContainer from './SidebarCompanyAvatarContainer';
import MessageButton from './MessageButtonContainer';
import DashboardButton from './DashboardButtonContainer';
import SidebarButton from './SidebarButton';
import SidebarAddOnsContainer from './SidebarAddOnsContainer';
import UISwitchSidebarButton from './UISwitchSidebarButton';
import UPDATE_CURRENT_UI from './mutations';

const SidebarLayout = styled.div`
  display: inline-flex;
  flex-direction: row;
  height: 100%;
  position: relative;
`;

const PrimaryPanel = styled.div`
  background-color: ${colors.charcoal};
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
  position: relative;
  height: 100%;
  overflow-y: auto;
  width: 64px;
  z-index: 2;

  @media (min-width: 1441px) {
    width: 264px;
  }
`;

const SidebarButtonGroupSeparatorWrapper = styled.div`
  padding: 8px 20px;
`;

const SidebarButtonGroupSeparator = styled.div`
  background-color: ${colors.ash};
  height: 1px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  flex: 0 0 auto;
  margin-top: ${props => (props.alignToBottom ? 'auto' : 0)};
`;

type SidebarProps = {
  clientId: string,
  history: {
    push: string => void,
    location: {
      pathname: string,
      search?: string,
    },
  },
};

/**
 * Provides history-aware links and hierarchy to nav items.
 */
const Sidebar = ({ clientId, history }: SidebarProps) => {
  const showUISwitchNavItem = localStorage.getItem('isNewUISwitchBannerVisible');
  const mutation = UPDATE_CURRENT_UI;
  const switchToNewUi = client => {
    const variables = { clientId: client, unifiedBanking: true };
    return graphqlClient.mutate({ mutation, variables }).then(result => {
      if (result.data.selfServeUiSwitch) {
        return window.location.replace(`${env.location.cwa}/client/${clientId}/welcome`);
      }
      return result;
    });
  };

  return (
    <SidebarLayout>
      <PrimaryPanel>
        <SidebarCompanyAvatarContainer clientId={clientId} />
        <ButtonGroup>
          <MessageButton clientId={clientId} history={history} />
          <SidebarButton
            testId="reports-sidebar-link"
            isActive={history.location.pathname.includes('/reports')}
            icon="chart"
            onClick={() => history.push('/reports/summary')}
            text="Reports"
          />
          <SidebarButton
            appcuesElementId="appcues-button-documents"
            testId="documents-sidebar-link"
            isActive={history.location.pathname.includes('/documents')}
            icon="document"
            onClick={() => history.push('/documents/uploads')}
            text="Documents"
          />
          <SidebarButton
            appcuesElementId="appcues-button-accounts"
            testId="accounts-sidebar-link"
            isActive={history.location.pathname.includes('/accounts')}
            icon="wallet"
            onClick={() => history.push('/accounts/connections')}
            text="Accounts"
          />
        </ButtonGroup>
        <ButtonGroup alignToBottom>
          <DashboardButton />
          <SidebarButtonGroupSeparatorWrapper>
            <SidebarButtonGroupSeparator />
          </SidebarButtonGroupSeparatorWrapper>
          {showUISwitchNavItem === 'false' && (
            <UISwitchSidebarButton
              appcuesElementId="appcues-button-ui-switch"
              icon="responsiveDesign"
              isDistinctColor
              onClick={() => switchToNewUi(clientId)}
              text="New UI"
              expandedText="Switch to the new Bench!"
              heapId="cwa-switch-new-ui-sidebar-button"
            />
          )}
          <SidebarButton
            appcuesElementId="appcues-button-refer-a-friend"
            isActive={history.location.pathname.includes('/refer-a-friend')}
            icon="gift"
            isDistinctColor
            onClick={() => history.push('/settings/refer-a-friend')}
            text="Refer"
            expandedText="Referral Rewards"
            heapId="cwa-referral-rewards-sidebar-button"
          />
          <SidebarAddOnsContainer clientId={clientId} />
          <SidebarButton
            testId="settings-sidebar-link"
            isActive={
              history.location.pathname.includes('/settings') &&
              !history.location.pathname.includes('/refer-a-friend')
            }
            icon="cog"
            onClick={() => history.push('/settings/profile')}
            text="Settings"
          />
          <SidebarButton
            testId="logout-sidebar-link"
            icon="logOut"
            onClick={() => {
              window.localStorage.removeItem('isNewUISwitchBannerVisible');
              window.sessionStorage.removeItem('user_logged_in');
              window.location = `${env.location.websiteRoot}/logout`;
            }}
            text="Log Out"
          />
        </ButtonGroup>
      </PrimaryPanel>
    </SidebarLayout>
  );
};

export default Sidebar;
